/**
 * Countdown formatting.
 *
 * The display is deliberately coarse while the auction is far away and gets
 * finer as the end approaches, so the number on screen is always one the user
 * can act on. Bands, from the top down:
 *
 *   >= 36h          whole days, rounded to nearest   "3 days"
 *   24h .. 36h      whole hours                      "24 hours", "30 hours"
 *   1h  .. 24h      hours + minutes                  "2 hours and 25 minutes"
 *   5m  .. 1h       whole minutes                    "45 minutes"
 *   1m  .. 5m       minutes + seconds                "3 minutes and 42 seconds"
 *   0   .. 1m       whole seconds                    "42 seconds"
 *   <= 0            ended                            "Ended"
 *
 * Boundary notes:
 *
 * - The day/hour handoff sits at 36h (1.5 days). Days round to nearest, so the
 *   first day value above the boundary is "2 days" and the display never jumps
 *   backwards as it crosses: "... 35 hours" is preceded by "2 days". A
 *   consequence is that "1 day" is never rendered -- everything from 24h to 36h
 *   is shown as hours, which is what makes 24h32m read "24 hours".
 * - Everything below a day floors, so hours/minutes/seconds never overstate the
 *   time left. Only the multi-day value rounds, where half-day precision is
 *   noise anyway.
 * - A zero sub-unit is dropped: "2 hours", not "2 hours and 0 minutes".
 */

export const SECOND_MS = 1000;
export const MINUTE_MS = 60 * SECOND_MS;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;

/** Lower edge of each band, in ms remaining. */
const DAYS_FROM = 36 * HOUR_MS;
const HOURS_FROM = 24 * HOUR_MS;
const HOURS_MINUTES_FROM = HOUR_MS;
const MINUTES_FROM = 5 * MINUTE_MS;
const MINUTES_SECONDS_FROM = MINUTE_MS;

export type CountdownUnit = "day" | "hour" | "minute" | "second";

export interface CountdownSegment {
  value: number;
  unit: CountdownUnit;
  /** Full unit word, already pluralised for `value`: "hours", "1 minute". */
  label: string;
  /** Abbreviated unit word for tight layouts: "hr", "min", "sec". */
  short: string;
}

export interface FormattedCountdown {
  ended: boolean;
  segments: CountdownSegment[];
  /** Plain language, for aria-labels and body copy: "2 hours and 25 minutes". */
  text: string;
  /** Abbreviated, for narrow displays: "2 hr 25 min". */
  shortText: string;
}

const UNITS: Record<
  CountdownUnit,
  { one: string; many: string; short: string }
> = {
  day: { one: "day", many: "days", short: "" },
  hour: { one: "hour", many: "hours", short: "hr" },
  minute: { one: "minute", many: "minutes", short: "min" },
  second: { one: "second", many: "seconds", short: "sec" },
};

type Band =
  | "days"
  | "hours"
  | "hoursMinutes"
  | "minutes"
  | "minutesSeconds"
  | "seconds";

/** The band a given remaining time falls in, plus that band's lower edge. */
function bandFor(remainingMs: number): { band: Band; from: number } {
  if (remainingMs >= DAYS_FROM) return { band: "days", from: DAYS_FROM };
  if (remainingMs >= HOURS_FROM) return { band: "hours", from: HOURS_FROM };
  if (remainingMs >= HOURS_MINUTES_FROM)
    return { band: "hoursMinutes", from: HOURS_MINUTES_FROM };
  if (remainingMs >= MINUTES_FROM) return { band: "minutes", from: MINUTES_FROM };
  if (remainingMs >= MINUTES_SECONDS_FROM)
    return { band: "minutesSeconds", from: MINUTES_SECONDS_FROM };
  return { band: "seconds", from: 0 };
}

function segment(value: number, unit: CountdownUnit): CountdownSegment {
  const words = UNITS[unit];
  const label = value === 1 ? words.one : words.many;

  return { value, unit, label, short: words.short || label };
}

function build(segments: CountdownSegment[]): FormattedCountdown {
  return {
    ended: false,
    segments,
    text: segments.map((s) => `${s.value} ${s.label}`).join(" and "),
    shortText: segments.map((s) => `${s.value} ${s.short}`).join(" "),
  };
}

/**
 * A zero-but-not-ended display, used for the brief grace window after the
 * target passes while the caller reloads and decides what happens next.
 */
export function zeroCountdown(): FormattedCountdown {
  return build([segment(0, "second")]);
}

/** Format the time remaining. Anything at or past the target reads "Ended". */
export function formatCountdown(remainingMs: number): FormattedCountdown {
  if (remainingMs <= 0) {
    return { ended: true, segments: [], text: "Ended", shortText: "Ended" };
  }

  const { band } = bandFor(remainingMs);

  switch (band) {
    case "days":
      return build([segment(Math.round(remainingMs / DAY_MS), "day")]);

    case "hours":
      return build([segment(Math.floor(remainingMs / HOUR_MS), "hour")]);

    case "hoursMinutes": {
      const hours = Math.floor(remainingMs / HOUR_MS);
      const minutes = Math.floor((remainingMs % HOUR_MS) / MINUTE_MS);

      return build(
        minutes > 0
          ? [segment(hours, "hour"), segment(minutes, "minute")]
          : [segment(hours, "hour")],
      );
    }

    case "minutes":
      return build([segment(Math.floor(remainingMs / MINUTE_MS), "minute")]);

    case "minutesSeconds": {
      const minutes = Math.floor(remainingMs / MINUTE_MS);
      const seconds = Math.floor((remainingMs % MINUTE_MS) / SECOND_MS);

      return build(
        seconds > 0
          ? [segment(minutes, "minute"), segment(seconds, "second")]
          : [segment(minutes, "minute")],
      );
    }

    default:
      return build([segment(Math.floor(remainingMs / SECOND_MS), "second")]);
  }
}

/**
 * How long a floored unit stays on screen: `floor(value / unit)` drops as soon
 * as `value` falls below the multiple it is sitting on, so a value that is
 * already an exact multiple changes on the very next millisecond.
 */
function untilFloorDrops(value: number, unit: number): number {
  return value % unit;
}

/**
 * Milliseconds until `formatCountdown` would produce different text, so the
 * timer only wakes as often as the displayed granularity requires: every second
 * while seconds are on screen, every minute while minutes are, and so on. The
 * result is also capped at the current band's lower edge so a coarse tick can
 * never sleep through the switch to a finer one.
 */
export function countdownTickMs(remainingMs: number): number {
  if (remainingMs <= 0) {
    return SECOND_MS;
  }

  const { band, from } = bandFor(remainingMs);
  let granularity: number;

  switch (band) {
    case "days": {
      // Days round, so the text changes at the half-day mark, not the whole.
      const days = Math.round(remainingMs / DAY_MS);
      granularity = remainingMs - (days - 0.5) * DAY_MS;
      break;
    }

    case "hours":
      granularity = untilFloorDrops(remainingMs, HOUR_MS);
      break;

    case "hoursMinutes":
    case "minutes":
      granularity = untilFloorDrops(remainingMs, MINUTE_MS);
      break;

    default:
      granularity = untilFloorDrops(remainingMs, SECOND_MS);
  }

  return Math.max(1, Math.min(granularity, remainingMs - from));
}
